import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api/client';

/**
 * Hook para cargar el logo de un tenant de forma autenticada.
 *
 * El endpoint GET /api/v1/admin/tenants/{id}/logo requiere Bearer token.
 * Un <img src="/api/.../logo"> directo no envia el header y falla con 401.
 * Este hook hace fetch con apiClient (inyecta Authorization) como blob y
 * crea un object URL para usar en <img src>.
 */
export function useTenantLogo(tenantId: string | undefined, logoUrl: string | null | undefined) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || !logoUrl) {
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    let cancelled = false;
    let currentUrl: string | null = null;
    setIsLoading(true);

    apiClient
      .get(logoUrl, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob: Blob = res.data;
        if (!blob.type.startsWith('image/')) {
          setObjectUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          return;
        }
        currentUrl = URL.createObjectURL(blob);
        setObjectUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return currentUrl;
        });
      })
      .catch(() => {
        if (!cancelled)
          setObjectUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentUrl) {
        try {
          URL.revokeObjectURL(currentUrl);
        } catch {
          // ignorar si ya fue revocado
        }
      }
    };
  }, [tenantId, logoUrl]);

  return { objectUrl, isLoading };
}
